"use client";

import { useState } from "react";
import { prepararAntispam, tokenAntispam } from "@/lib/antispam/cliente";
import { CampoTrampa } from "@/components/antispam/campo-trampa";

import { textos, type Idioma } from "@/lib/directorio/idiomas";
import { TEXTOS_RECLAMAR } from "@/lib/directorio/idiomas-reclamar";

/**
 * El formulario para opinar de un sitio.
 *
 * Nada de lo que se escribe aqui aparece en la ficha hasta que alguien lo
 * aprueba, y se dice antes de enviar: prometer publicacion inmediata y luego no
 * publicar es peor que avisar.
 */
export function FormularioOpinion({
  listingId,
  idioma,
}: {
  listingId: string;
  idioma: Idioma;
}) {
  const t = textos(idioma);
  // El nombre y el correo ya estan traducidos para reclamar una ficha.
  const r = TEXTOS_RECLAMAR[idioma];
  const [abierto, setAbierto] = useState(false);
  const [nota, setNota] = useState(0);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function enviar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    if (nota === 0) return;
    setError(null);
    setEnviando(true);
    const datos = new FormData(evento.currentTarget);
    try {
      const token = await tokenAntispam("opinion");
      const respuesta = await fetch("/api/directorio/opinion", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          listingId,
          rating: nota,
          nombre: datos.get("nombre"),
          email: datos.get("email"),
          titulo: datos.get("titulo"),
          opinion: datos.get("opinion"),
          sitio_web: datos.get("sitio_web"),
          token,
        }),
      });
      const cuerpo = await respuesta.json().catch(() => ({}));
      if (!respuesta.ok) throw new Error(cuerpo.error || "No se ha podido enviar");
      setEnviado(true);
    } catch (fallo: any) {
      setError(fallo.message || "No se ha podido enviar");
    } finally {
      setEnviando(false);
    }
  }

  if (enviado) {
    return (
      <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4">
        <p className="font-medium text-zinc-900">{t.opinionEnviada}</p>
        <p className="mt-1 text-sm text-zinc-600">{t.opinionEnRevision}</p>
      </div>
    );
  }

  if (!abierto) {
    return (
      <button
        onClick={() => setAbierto(true)}
        className="rounded-lg border border-zinc-200 px-5 py-2.5 text-sm font-medium text-zinc-700 transition-colors hover:border-zinc-300 hover:text-zinc-900"
      >
        {t.escribirOpinion}
      </button>
    );
  }

  const campo =
    "mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm outline-none transition-colors focus:border-zinc-400";

  return (
    <form onSubmit={enviar} onFocus={prepararAntispam} className="relative space-y-4">
      <CampoTrampa />
      <div>
        <span className="text-sm font-medium text-zinc-700">{t.campoValoracion}</span>
        <div className="mt-1 flex gap-1">
          {[1, 2, 3, 4, 5].map((i) => (
            <button
              key={i}
              type="button"
              onClick={() => setNota(i)}
              aria-label={`${i}`}
              aria-pressed={nota === i}
              className="p-0.5"
            >
              {/* Circulos y no estrellas: la unica estrella de una ficha es la
                  de Michelin. Aqui, al ser un control de entrada, un glifo
                  repetido si tiene sentido: hay que poder pinchar en el 4. */}
              <span
                className={
                  nota >= i
                    ? "block h-6 w-6 rounded-full bg-zinc-900"
                    : "block h-6 w-6 rounded-full border-2 border-zinc-300"
                }
              />
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="font-medium text-zinc-700">{r.campoNombre}</span>
          <input name="nombre" required maxLength={80} className={campo} />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-zinc-700">{r.campoEmail}</span>
          <input name="email" type="email" required maxLength={200} className={campo} />
        </label>
      </div>

      <label className="block text-sm">
        <span className="font-medium text-zinc-700">{t.campoTitulo}</span>
        <input name="titulo" maxLength={120} className={campo} />
      </label>

      <label className="block text-sm">
        <span className="font-medium text-zinc-700">{t.campoOpinion}</span>
        <textarea name="opinion" required rows={4} minLength={20} maxLength={2000} className={campo} />
      </label>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={enviando || nota === 0}
          className="rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-[var(--primary-hover)] disabled:opacity-60"
        >
          {enviando ? "…" : t.publicarOpinion}
        </button>
        <span className="text-xs text-zinc-400">{t.opinionEnRevision}</span>
      </div>
    </form>
  );
}
